import { useState } from "react";

function PrivacyPolicy({ onBack, config }) {
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    {
      id: "collection",
      title: "1. Information We Collect",
      content: `
**Personal Information:**
- Name and username
- Email address
- Password (encrypted)
- Payment information (processed securely by Stripe)

**Educational Data:**
- Course progress and completion status
- Quiz and exam scores
- Certificates earned
- Study schedules and notes

**Technical Data:**
- Device type and browser information
- IP address
- Usage patterns and preferences
      `
    },
    {
      id: "use",
      title: "2. How We Use Your Information",
      content: `
We use collected information to:

- Provide and maintain our educational services
- Process subscriptions and payments
- Track your learning progress
- Generate certificates of completion
- Send important updates about your account
- Improve our curriculum and platform
- Comply with legal obligations

**We do NOT:**
- Sell your personal information to third parties
- Share your data for advertising purposes
- Use your information for any purpose other than providing our services
      `
    },
    {
      id: "children",
      title: "3. Children's Privacy (COPPA Compliance)",
      content: `
The Yisrael Learning Center serves students of all ages, including children under 13. We take children's privacy seriously:

**For Students Under 13:**
- Parental consent is required for account creation
- Parents can access and manage their child's account through the Parent Portal
- We collect only information necessary for educational purposes
- Parents can request deletion of their child's data at any time

**Parent Portal Features:**
- View your child's learning progress
- Monitor quiz scores and activity
- Link/unlink child accounts
- Request data deletion

**Contact us** if you believe we have collected information from a child without parental consent.
      `
    },
    {
      id: "sharing",
      title: "4. Information Sharing",
      content: `
We share your information only in limited circumstances:

**Service Providers:**
- **Stripe**: For secure payment processing
- **MongoDB**: For secure data storage
- **Cloud hosting providers**: For platform infrastructure

**Legal Requirements:**
We may disclose information if required by law, court order, or government request.

**With Your Consent:**
We may share information with third parties when you explicitly authorize us to do so.

**We Never:**
- Sell personal information
- Share data with advertisers
- Disclose student records to unauthorized parties
      `
    },
    {
      id: "security",
      title: "5. Data Security",
      content: `
We implement robust security measures to protect your data:

**Technical Safeguards:**
- SSL/TLS encryption for all data transmission
- Encrypted password storage (bcrypt hashing)
- Secure payment processing through Stripe (PCI-DSS compliant)
- Regular security audits and updates

**Access Controls:**
- Role-based access to sensitive data
- Admin authentication requirements
- Session management and automatic logout

**Data Retention:**
- Account data retained while subscription is active
- You may request data deletion at any time
- Backup data retained for 30 days after deletion request
      `
    },
    {
      id: "cookies",
      title: "6. Cookies and Tracking",
      content: `
**Essential Cookies:**
We use essential cookies to:
- Maintain your login session
- Remember your preferences
- Ensure platform security

**We Do NOT Use:**
- Advertising or marketing cookies
- Third-party tracking cookies
- Analytics that identify individual users

**Local Storage:**
We store some data locally on your device:
- Study notes (saved in your browser)
- Lesson progress markers
- User preferences

You can clear this data through your browser settings.
      `
    },
    {
      id: "rights",
      title: "7. Your Rights",
      content: `
You have the following rights regarding your data:

**Access:** Request a copy of your personal data
**Correction:** Update or correct inaccurate information
**Deletion:** Request deletion of your account and data
**Portability:** Receive your data in a portable format
**Objection:** Object to certain data processing activities

**To Exercise Your Rights:**
- Email: Yisraellearningcenter@gmail.com
- Use the account settings in your dashboard
- Contact our support team

We will respond to requests within 30 days.
      `
    },
    {
      id: "subscriptions",
      title: "8. Subscription & Payment Data",
      content: `
**Payment Processing:**
All payments are processed securely through Stripe. We do NOT store:
- Full credit card numbers
- CVV/security codes
- Bank account details

**What We Store:**
- Stripe customer ID (for subscription management)
- Subscription status and renewal dates
- Payment history (amounts and dates only)

**Trial Period:**
- 7-day free trial for new users
- No payment required during trial
- Automatic reminder before trial ends

**Cancellation:**
- Cancel anytime through your dashboard
- Access continues until end of billing period
- No refunds for partial months
      `
    },
    {
      id: "changes",
      title: "9. Changes to This Policy",
      content: `
We may update this Privacy Policy from time to time. When we make changes:

- We will update the "Last Updated" date at the top of this page
- For significant changes, we will notify you via email
- Continued use of the platform after changes constitutes acceptance

**Previous Versions:**
You may request previous versions of this policy by contacting us.
      `
    },
    {
      id: "contact",
      title: "10. Contact Us",
      content: `
If you have questions about this Privacy Policy or our data practices:

**Email:** Yisraellearningcenter@gmail.com

**Mailing Address:**
Yisrael Learning Center
Twelve Tribes of Yisrael Ministry

**Response Time:** We aim to respond to all inquiries within 48 hours.

**For Parents:** If you have concerns about your child's data, please include "Parent Inquiry" in your subject line for priority handling.
      `
    }
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#111", padding: "20px" }}>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Header */}
        <div className="card" style={{
          background: "linear-gradient(135deg, #1a1a2a 0%, #2a1a2a 100%)",
          borderTop: "4px solid #FFD700",
          textAlign: "center"
        }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                position: "absolute",
                left: "20px",
                top: "20px",
                background: "#333",
                padding: "8px 16px",
                fontSize: "0.9rem"
              }}
            >
              ← Back
            </button>
          )}
          
          <h1 style={{ color: "#FFD700", marginBottom: "10px" }}>
            Privacy Policy
          </h1>
          <p style={{ color: "#aaa", marginBottom: "5px" }}>
            {config?.CHURCH_NAME || "Yisrael Learning Center"}
          </p>
          <p style={{ color: "#999", fontSize: "0.9rem" }}>
            Last Updated: February 26, 2026
          </p>
        </div>

        {/* Introduction */}
        <div className="card" style={{ background: "#1a2a1a", borderLeft: "4px solid #90EE90" }}>
          <p style={{ color: "#eee", lineHeight: "1.8", margin: 0 }}>
            The Yisrael Learning Center ("we," "our," or "us") is committed to protecting your privacy 
            and the privacy of our students, including children. This Privacy Policy explains how we 
            collect, use, disclose, and safeguard your information when you use our educational platform.
          </p>
        </div>

        {/* Quick Links */}
        <div className="card" style={{ background: "#1a1a1a" }}>
          <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>
            📋 Quick Navigation
          </h3>
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "10px"
          }}>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  setActiveSection(section.id);
                  document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                }}
                style={{
                  background: "#222",
                  color: "#eee",
                  padding: "10px 15px",
                  textAlign: "left",
                  fontSize: "0.9rem",
                  marginTop: 0,
                  border: activeSection === section.id ? "2px solid #87CEEB" : "1px solid #333"
                }}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Policy Sections */}
        {sections.map((section) => (
          <div 
            key={section.id} 
            id={section.id}
            className="card"
            style={{
              background: "#1a1a1a",
              borderLeft: "4px solid #FFD700"
            }}
          >
            <h2 style={{ color: "#FFD700", marginBottom: "20px" }}>
              {section.title}
            </h2>
            <div style={{ color: "#ddd", lineHeight: "1.9" }}>
              {section.content.split('\n').map((line, i) => {
                if (line.startsWith('**') && line.endsWith('**')) {
                  return (
                    <h4 key={i} style={{ color: "#90EE90", marginTop: "15px", marginBottom: "10px" }}>
                      {line.replace(/\*\*/g, '')}
                    </h4>
                  );
                } else if (line.startsWith('- ')) {
                  return (
                    <div key={i} style={{ 
                      paddingLeft: "20px", 
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "flex-start"
                    }}>
                      <span style={{ color: "#FFD700", marginRight: "10px" }}>•</span>
                      <span>{line.substring(2)}</span>
                    </div>
                  );
                } else if (line.trim()) {
                  return <p key={i} style={{ marginBottom: "10px" }}>{line}</p>;
                }
                return null;
              })}
            </div>
          </div>
        ))}

        {/* Footer */}
        <div className="card" style={{ 
          background: "#0a0a0a", 
          textAlign: "center",
          borderTop: "2px solid #333"
        }}>
          <p style={{ color: "#999", marginBottom: "15px" }}>
            By using the Yisrael Learning Center, you agree to this Privacy Policy.
          </p>
          <p style={{ color: "#FFD700", marginBottom: "10px" }}>
            {config?.CHURCH_NAME || "Twelve Tribes of Yisrael"}
          </p>
          <p style={{ color: "#999", fontSize: "0.85rem" }}>
            © {new Date().getFullYear()} Yisrael Learning Center. All rights reserved.
          </p>
          
          {onBack && (
            <button
              onClick={onBack}
              style={{
                marginTop: "20px",
                background: "#333",
                padding: "12px 30px"
              }}
            >
              ← Return to Platform
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
